import React, { useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import {
  PrecisionManufacturingOutlined,
} from "@mui/icons-material";
import { dt } from "../../../shared/themes/redesignTokens";
import { CatalogModelListItem } from "../models/models";
import { formatManufacturerWithHierarchy } from "../../../shared/utils/machineUtils";
import useTranslation from "../../../shared/hooks/useTranslation";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";
import OemStar from "./OemStar";
import SparkIcon from "./SparkIcon";
import CatalogReadinessRow from "./CatalogReadinessRow";
import MidasReadinessPopup from "./readiness/MidasReadinessPopup";
import { bannerTier as deriveBannerTier } from "../utils/midasReadiness";

interface CatalogModelCardProps {
  model: CatalogModelListItem;
  index: number;
  onClick: (model: CatalogModelListItem) => void;
  onAddMachine?: (model: CatalogModelListItem) => void;
}

const categoryColorMap: Record<string, string> = {
  "machining-centers": dt.blue,
  "turning-machines": "#8E44AD",
  "grinding-machines": "#E67E22",
  "edm": "#27AE60",
  "laser-machines": "#E74C3C",
};

const categoryLabelMap: Record<string, string> = {
  "machining-centers": "MILLING",
  "turning-machines": "TURNING",
  "grinding-machines": "GRINDING",
  "edm": "EDM",
  "laser-machines": "LASER",
};

const platformCountDt = {
  bg: "rgba(39, 174, 96, 0.08)",
  text: "#27AE60",
  border: "rgba(39, 174, 96, 0.15)",
};

// --- DR-1/DR-2: MIDAS Readiness banner tokens (exact values from Paper artboard v2-1) ---
const oemRibbon = {
  border: "#E8D9AE",   // gold hairline card border
  bg: "#FBF4DF",       // ribbon background
  star: "#B98A1F",     // ribbon star fill
  text: "#A87F1E",     // ribbon label
  hoverBorder: "#D9C481",
};

// DR-2: blue "AI READY · MIDAS" ribbon (Tier 2 — gold outranks this)
const aiRibbon = {
  border: "#AED6F1",   // blue hairline card border
  bg: "#EBF5FB",       // ribbon background
  spark: "#3498DB",    // ribbon spark fill
  text: "#2E86DE",     // ribbon label
  hoverBorder: "#7FB8E0",
};

const CatalogModelCard: React.FC<CatalogModelCardProps> = ({
  model,
  index,
  onClick,
  onAddMachine,
}) => {
  const isPhone = useMediaQuery("(max-width:768px)");
  const { translate } = useTranslation("myMachinery");
  const [readinessOpen, setReadinessOpen] = useState(false);

  const categoryColor = categoryColorMap[model.categoryCode] || dt.blue;
  const categoryLabel = categoryLabelMap[model.categoryCode] || model.categoryCode;
  const unitsOnPlatform = (model as any).unitsOnPlatform ?? 0;

  // DR-1/DR-2: MIDAS Readiness banner tier from the shared util (single source
  // of truth). Rule: max ONE banner per card, gold OEM outranks blue AI READY.
  const bannerTier = deriveBannerTier(model); // "oem-gold" | "ai-blue" | null
  const cardPadding = isPhone ? "12px" : "16px";
  const activeRibbon = bannerTier === "oem-gold" ? oemRibbon : bannerTier === "ai-blue" ? aiRibbon : null;

  return (
    <Box
      onClick={() => onClick(model)}
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: isPhone ? dt.radiusSm : dt.radius,
        border: `1px solid ${activeRibbon ? activeRibbon.border : dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        padding: cardPadding,
        // Clip the full-bleed ribbon to the card's rounded corners
        overflow: bannerTier ? "hidden" : undefined,
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${Math.min(index * 0.06, 0.6)}s both`,
        "&:hover": {
          boxShadow: dt.cardShadowHover,
          transform: "translateY(-4px)",
          borderColor: activeRibbon ? activeRibbon.hoverBorder : `${categoryColor}40`,
        },
      }}
    >
      {/* DR-1/DR-2: MIDAS Readiness ribbon — full-bleed top banner, max one.
          Gold "FULL OEM SUPPORT" (Tier 1) OR blue "AI READY · MIDAS" (Tier 2). */}
      {activeRibbon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            backgroundColor: activeRibbon.bg,
            padding: "5px 10px",
            // Negate card padding so the ribbon spans full width and sits flush at top
            margin: `-${cardPadding} -${cardPadding} 12px -${cardPadding}`,
          }}
        >
          {bannerTier === "oem-gold" ? (
            <OemStar size={10} fill={oemRibbon.star} />
          ) : (
            <SparkIcon size={10} fill={aiRibbon.spark} />
          )}
          <Box
            sx={{
              fontSize: "10px",
              lineHeight: "12px",
              fontWeight: 600,
              fontFamily: dt.font,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: activeRibbon.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {bannerTier === "oem-gold"
              ? `${translate("fullOemSupport")}${model.oemPartnerName ? ` · ${model.oemPartnerName}` : ""}`
              : translate("aiReadyMidas")}
          </Box>
        </Box>
      )}

      {/* Top row: Category badge + Platform count */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        {/* Category badge */}
        <Box
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            fontFamily: dt.font,
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            color: categoryColor,
            backgroundColor: `${categoryColor}10`,
            borderRadius: "6px",
            padding: "3px 8px",
          }}
        >
          {categoryLabel}
        </Box>

        {/* Platform count badge (only when > 0) */}
        {unitsOnPlatform > 0 && (
          <Box
            sx={{
              fontSize: "11px",
              fontFamily: dt.fontMono,
              fontWeight: 600,
              color: platformCountDt.text,
              backgroundColor: platformCountDt.bg,
              border: `1px solid ${platformCountDt.border}`,
              borderRadius: "10px",
              padding: "2px 8px",
            }}
          >
            {unitsOnPlatform} units
          </Box>
        )}
      </Box>

      {/* Main content: Image placeholder + text */}
      <Box sx={{ display: "flex", gap: 1.5 }}>
        {/* Image placeholder */}
        <Box
          sx={{
            width: isPhone ? 60 : 80,
            height: isPhone ? 60 : 64,
            borderRadius: dt.radiusSm,
            backgroundColor: `${categoryColor}08`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {(model.imageStandardUrl || model.imageUrl) ? (
            <img
              src={model.imageStandardUrl || model.imageUrl || ""}
              alt={model.modelName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <PrecisionManufacturingOutlined
              sx={{ fontSize: isPhone ? 24 : 28, color: `${categoryColor}60` }}
            />
          )}
        </Box>

        {/* Text info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Model name */}
          <Box
            sx={{
              fontSize: isPhone ? "14px" : "15px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: dt.textPrimary,
              letterSpacing: "-0.1px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {model.modelName}
          </Box>

          {/* Manufacturer name */}
          <Box
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              fontFamily: dt.font,
              color: dt.textSecondary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mt: 0.25,
            }}
          >
            {model.manufacturer
              ? formatManufacturerWithHierarchy(
                  model.manufacturer.name,
                  model.manufacturer.parentManufacturerName
                )
              : ""}
          </Box>

          {/* SubType */}
          {model.subType && (
            <Box
              sx={{
                fontSize: "11px",
                fontWeight: 400,
                fontFamily: dt.font,
                color: dt.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                mt: 0.25,
              }}
            >
              {model.subType}
            </Box>
          )}
        </Box>
      </Box>

      {/* Key specs row (Phase 7F) */}
      {(model.maxSpindleRpm || model.spindleHp || model.toolCapacity) && (
        <Box
          sx={{
            display: "flex",
            gap: isPhone ? "8px" : "12px",
            flexWrap: "wrap",
            mt: 1,
          }}
        >
          {model.maxSpindleRpm && (
            <Box sx={{ fontSize: "11px", fontFamily: 'var(--font-mono)', fontWeight: 500, color: dt.textSecondary }}>
              {model.maxSpindleRpm.toLocaleString()} <Box component="span" sx={{ color: dt.textMuted, fontSize: "10px" }}>RPM</Box>
            </Box>
          )}
          {model.spindleHp && (
            <Box sx={{ fontSize: "11px", fontFamily: 'var(--font-mono)', fontWeight: 500, color: dt.textSecondary }}>
              {model.spindleHp} <Box component="span" sx={{ color: dt.textMuted, fontSize: "10px" }}>HP</Box>
            </Box>
          )}
          {model.toolCapacity && (
            <Box sx={{ fontSize: "11px", fontFamily: 'var(--font-mono)', fontWeight: 500, color: dt.textSecondary }}>
              {model.toolCapacity} <Box component="span" sx={{ color: dt.textMuted, fontSize: "10px" }}>tools</Box>
            </Box>
          )}
          {model.taperTypeName && (
            <Box sx={{ fontSize: "10px", fontWeight: 600, fontFamily: 'var(--font-mono)', color: "#8E44AD", backgroundColor: "rgba(142, 68, 173, 0.08)", borderRadius: "8px", padding: "1px 6px" }}>
              {model.taperTypeName}
            </Box>
          )}
        </Box>
      )}

      {/* DR-2: MIDAS Readiness row (dots + doc count) — present on EVERY card. */}
      <Box sx={{ mt: 1.5 }}>
        <CatalogReadinessRow model={model} />
      </Box>

      {/* DR-3: "View details ›" — right-aligned link opening the shared readiness
          popup (self-configures per model). stopPropagation so it does NOT trigger
          the card's navigate-to-detail onClick. */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "6px" }}>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            setReadinessOpen(true);
          }}
          sx={{
            fontSize: "10px",
            lineHeight: "12px",
            fontWeight: 600,
            fontFamily: dt.font,
            color: "#2E90E5",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {translate("viewDetails")} ›
        </Box>
      </Box>

      <MidasReadinessPopup
        open={readinessOpen}
        onClose={() => setReadinessOpen(false)}
        readiness={model.readiness}
        isOemPartner={model.isOemPartner}
        oemPartnerName={model.oemPartnerName}
        machineOrModelName={model.modelName}
        manufacturerName={model.manufacturer?.name}
      />

      {/* Divider */}
      {!isPhone && (
        <Box sx={{ borderTop: `1px solid ${dt.rowBorder}`, margin: "12px 0 8px 0" }} />
      )}

      {/* Footer (desktop/tablet only) */}
      {!isPhone && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Left: Units on platform / New to catalog */}
          <Box
            sx={{
              fontSize: "11px",
              fontFamily: dt.fontMono,
              fontWeight: 600,
              color: unitsOnPlatform > 0 ? platformCountDt.text : dt.textMuted,
            }}
          >
            {unitsOnPlatform > 0
              ? translate("unitsOnPlatform", { count: unitsOnPlatform })
              : translate("newToCatalog")}
          </Box>

          {/* Right: Add button + View Specs link */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {onAddMachine && (
              <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddMachine(model);
                  }}
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    fontFamily: dt.font,
                    color: dt.blue,
                    backgroundColor: `${dt.blue}10`,
                    borderRadius: "10px",
                    padding: "3px 10px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      backgroundColor: `${dt.blue}20`,
                    },
                  }}
                >
                  {translate("addToMyMachines")}
                </Box>
              </PermissionGuard>
            )}

          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CatalogModelCard;
